import MainContentBanner from '@/components/templates/landing/components/MainContentBanner';
import { MASS_EVENT_END_DATE_TIME } from '@/constants/etc';
import { DeskTop, Tablet } from '@/styles/layout';
import { isBeforeTargetDate } from '@/utils/date';
import Link from 'next/link';
import { useEffect, useRef, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

const MainBannerSection = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [opacity, setOpacity] = useState<number>(0);
  const [videoPlaying, setVideoPlaying] = useState<boolean>(true);
  const isBeforeMassEventEndDateTime = isBeforeTargetDate(MASS_EVENT_END_DATE_TIME);
  const shouldShowMassEventBanner = isBeforeMassEventEndDateTime;

  const onChangePlaying = (playing: boolean) => {
    setVideoPlaying(playing);
  };

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const calculate = () => {
      if (!ref.current) {
        return;
      }
      const sectionHeight = ref.current.clientHeight;
      const scrollY = window.scrollY;

      const scrollRatio = scrollY / sectionHeight;

      const opacity = scrollRatio > 1 ? 1 : scrollRatio;
      setOpacity(opacity);
    };

    window.addEventListener('scroll', calculate);
    return () => {
      window.removeEventListener('scroll', calculate);
    };
  }, []);

  return (
    <MainBanner ref={ref} opacity={opacity} shouldShowMassEventBanner={shouldShowMassEventBanner}>
      {shouldShowMassEventBanner ? (
        <>
          <MainContentBanner onChangePlaying={onChangePlaying} videoPlaying={videoPlaying} />
          {!videoPlaying && <ThumbnailOverlay opacity={opacity} />}
          <ContentWrapper>
            {!videoPlaying && (
              <Content>
                <MainCopyContainer>
                  <MainCopyrightImg src={'https://file.doctornow.co.kr/landing/catchphrase.png'} />
                </MainCopyContainer>
                <DownloadButton onClick={() => window.open('https://abr.ge/0os6ry')}>앱 다운로드</DownloadButton>
                <PlayButtonIcon onClick={() => onChangePlaying(true)} />
                <FloatingArrowDown />
              </Content>
            )}
          </ContentWrapper>
        </>
      ) : (
        <div className='flex max-w-[1080px] flex-col px-[24px] pt-[52px] pb-[40px] lg:pt-[128px] lg:pb-[96px]'>
          <div
            className='typography-ui-17sb typography-ui-14r mb-[12px] text-gray-400'
            style={{
              opacity: 1 - opacity,
            }}
          >
            * 2024 주간동아 <span className='typography-ui-14b'>한국고객만족도 비대면 진료 앱 1위</span>
          </div>
          <h1 className='typography-ui-28b lg:typography-ui-56b mb-[24px] text-gray-900'>
            대한민국 1위 비대면 진료 앱<br />
            아플 땐 닥터나우
          </h1>
          <div className='mb-[64px] flex flex-col justify-between gap-y-[40px] lg:flex-row'>
            <div className='typography-ui-22sb text-gray-500'>
              아픈 모든 순간, 닥터나우가
              <br />
              가장 빠르고 편리하게 해결해 드릴게요.
            </div>
            <div className='typography-ui-15sb lg:typography-ui-17sb flex h-[40px] w-fit items-center rounded-[8px] bg-primary-600 px-[16px] py-[8px] text-white lg:h-[56px] lg:rounded-[12px]'>
              <Link href={'/telemedicine/doctors'}>
                <a>서비스 둘러보기</a>
              </Link>
            </div>
          </div>
          <div
            style={{
              opacity: 1 - opacity,
            }}
          >
            <img
              src={'https://file.doctornow.co.kr/landing/main_banner_section.png'}
              alt='비대면 진료 앱 이미지'
              className={'h-auto max-h-[480px] w-full max-w-[1050px] rounded-[8px] lg:rounded-[32px]'}
            />
          </div>
        </div>
      )}
    </MainBanner>
  );
};

export default MainBannerSection;

const BottomUp = keyframes`
    0% {
        transform: translate(-50%, 20px);
    }
    100% {
        transform: translate(-50%, -20px);
    }
`;

const MainBanner = styled.div<{ opacity: number; shouldShowMassEventBanner: boolean }>`
  position: fixed;
  width: 100vw;
  height: ${({ shouldShowMassEventBanner }) => (shouldShowMassEventBanner ? 'calc(100vw * 9 / 16)' : '1089px')} !important;

  background-size: cover;
  background-position: center;
  background-color: ${({ shouldShowMassEventBanner }) => (shouldShowMassEventBanner ? 'black' : 'white')};

  display: flex;
  flex-direction: column;
  align-items: center;
  isolation: isolate;

  @media screen and (max-width: ${Tablet}) {
    width: 100%;
    height: 1089px !important;
    background-color: white;
  }

  &::after {
    content: '';
    position: absolute;
    background: #2d2e2f;
    z-index: 1;
    inset: 0;
    opacity: ${({ opacity }) => opacity * 1.1};
  }

  ${({ theme }) =>
    !theme.client.isMobile &&
    css`
      &::before {
        position: absolute;
        width: 0;
        height: 0;
        overflow: hidden;
        z-index: -1;

        content: url('https://file.doctornow.co.kr/landing/catchphrase.png')
          url('https://file.doctornow.co.kr/landing/cf-web-main-thumbnail.jpg');
      }
    `}
`;

const ContentWrapper = styled.div`
  position: absolute;
  width: 100%;
  max-width: 1080px;
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 0 20px;
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: calc((100vw * 9 / 16) / 35);

  @media screen and (max-width: ${Tablet}) {
    display: none;
  }
`;

const MainCopyContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: calc((100vw * 9 / 16) / 5.5);

  @media screen and (max-width: ${DeskTop}) {
    margin-top: calc((100vw * 9 / 16) / 20);
  }
`;

const MainCopyrightImg = styled.img`
  width: 28vw;
  object-fit: contain;
  transform: translateX(-11%);
`;

const DownloadButton = styled.button`
  width: 200px;
  height: 52px;
  border-radius: 8px;
  padding: 0 12px;

  ${({ theme }) => `
    background: rgba(255, 255, 255, 0.20);
    border: 1px solid ${theme.color.gray100};
    color: ${theme.color.white};
    ${theme.typography.UI17SB};
  `};
  @media screen and (max-width: ${Tablet}) {
    display: none;
  }
`;

const ThumbnailOverlay = styled.div<{ opacity: number }>`
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;

  background-size: cover;
  background-position: center;
  background-image: url('https://file.doctornow.co.kr/landing/cf-web-main-thumbnail.jpg');

  &::after {
    content: '';
    position: absolute;
    background: #2d2e2f;
    z-index: -1;
    inset: 0;
    opacity: ${({ opacity }) => opacity * 1.1};
  }

  @media screen and (max-width: ${Tablet}) {
    display: none;
  }
`;

const PlayButtonIcon = styled.div`
  position: absolute;
  width: 6vw;
  min-width: 100px;
  aspect-ratio: 1;
  background-image: url('https://file.doctornow.co.kr/landing/play-circle.png');
  opacity: 0.7;
  background-size: cover;
  background-position: center;
  cursor: pointer;
  top: 55%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const FloatingArrowDown = styled.div`
  position: absolute;
  bottom: 2.5%;
  left: 50%;
  width: 80px;
  height: 80px;
  background-image: url('https://file.doctornow.co.kr/landing/floating_arrow_down.png');
  opacity: 0.6;
  background-size: cover;
  background-position: center;
  animation: ${BottomUp} 1s infinite alternate;
`;
